/**
 * SMB shared runtime contracts.
 *
 * This package is intentionally self-contained so the control app and tenant
 * runtime can share a stable set of types without depending on the current
 * OpenClaw source tree.
 */

export type IsoTimestamp = string;

export * from "./control-plane-web.js";
export * from "./control-plane-paths.js";
export * from "./lightweight-automation.js";
export * from "./sessions.js";
export * from "./session-gateway-client.js";
export * from "./session-live.js";
export * from "./assistant-text-parser.js";
export * from "./tenant-app-user-roles.js";
import type { LightweightAutomationRecord } from "./lightweight-automation.js";
import type { TenantAppUserRoleKey } from "./tenant-app-user-roles.js";
export type HashString = string;
export type IdempotencyKey = string;
export type TenantId = string;
export type AgentId = string;
export type ConversationId = string;
export type WorkflowExecutionId = string;
export type RuntimeKey = string;
export type SnapshotGeneration = number;

export type JsonPrimitive = string | number | boolean | null;
export type JsonValue = JsonPrimitive | JsonObject | JsonValue[];
export type JsonObject = { [key: string]: JsonValue };

export type ExecutionClass = "chat_only" | "approved_actions" | "operator_maintenance";
export type WorkflowEngineKind = "local" | "temporal";
export type ConnectorActionKind = "read" | "write";
export type ConnectorCredentialMode = "service_account" | "delegated_user" | "admin_impersonation";
export type ConnectorAssuranceLevel = "level_0" | "level_1" | "level_2";
export type WorkflowExecutionStatus = "pending" | "running" | "succeeded" | "failed" | "canceled";
export type RuntimeApplyStatus = "applied" | "rejected";
export type ActorType = "operator_user" | "user" | "runtime" | "system";

export const DEFAULT_CONNECTOR_CREDENTIAL_MODE: ConnectorCredentialMode = "service_account";
export const DEFAULT_CONNECTOR_ASSURANCE_LEVEL: ConnectorAssuranceLevel = "level_0";

export function resolveConnectorCredentialMode(
  value?: ConnectorCredentialMode | null,
): ConnectorCredentialMode {
  return value ?? DEFAULT_CONNECTOR_CREDENTIAL_MODE;
}

export function resolveConnectorAssuranceLevel(
  value?: ConnectorAssuranceLevel | null,
): ConnectorAssuranceLevel {
  return value ?? DEFAULT_CONNECTOR_ASSURANCE_LEVEL;
}

export function compareConnectorAssuranceLevels(
  left: ConnectorAssuranceLevel,
  right: ConnectorAssuranceLevel,
): number {
  return connectorAssuranceLevelRank(left) - connectorAssuranceLevelRank(right);
}

export function isConnectorAssuranceSatisfied(params: {
  requiredAssuranceLevel: ConnectorAssuranceLevel;
  assuranceSatisfiedAt?: IsoTimestamp;
}): boolean {
  if (params.requiredAssuranceLevel === "level_2") {
    return (
      typeof params.assuranceSatisfiedAt === "string" && params.assuranceSatisfiedAt.length > 0
    );
  }
  return true;
}

// ---------------------------------------------------------------------------
// Secret reference contract (P4-002)
// ---------------------------------------------------------------------------

/**
 * Runtime secrets are always resolved through the control-plane API.
 */
export type SecretRefProvider = "control_plane";

/**
 * A typed, opaque reference to a secret managed outside the snapshot.
 *
 * Snapshots carry the reference, never the raw secret value.  The runtime
 * resolves the reference through its configured secret provider at execution
 * time.
 */
export interface SecretRef {
  /** Provider responsible for resolving this reference. */
  provider: SecretRefProvider;
  /** Opaque control-plane path or key (e.g. `"tenant_acme/hubspot-api-key"`). */
  path: string;
  /** Optional version pin for audit trails and deterministic rollback. */
  version?: string;
  /** Optional resolver hints that contain no secret material. */
  metadata?: JsonObject;
}

/** Canonical URI scheme: `secretref://<provider>/<path>[?version=<v>]` */
const SECRET_REF_SCHEME = "secretref://";

/** Build a `SecretRef` from explicit parts. */
export function createSecretRef(
  provider: SecretRefProvider,
  path: string,
  opts?: { version?: string; metadata?: JsonObject },
): SecretRef {
  const ref: SecretRef = { provider, path };
  if (opts?.version !== undefined) {
    ref.version = opts.version;
  }
  if (opts?.metadata !== undefined) {
    ref.metadata = opts.metadata;
  }
  return ref;
}

/**
 * Serialize a `SecretRef` to its canonical URI form.
 *
 * ```
 * secretref://control_plane/tenant_acme/hubspot-api-key
 * secretref://control_plane/tenant_acme/hubspot-api-key?version=3
 * ```
 */
export function formatSecretRefUri(ref: SecretRef): string {
  let uri = `${SECRET_REF_SCHEME}${ref.provider}/${ref.path}`;
  if (ref.version !== undefined) {
    uri += `?version=${encodeURIComponent(ref.version)}`;
  }
  return uri;
}

/**
 * Parse a canonical `secretref://` URI back into a `SecretRef`.
 *
 * Returns `null` if the URI does not match the expected scheme.
 */
export function parseSecretRefUri(uri: string): SecretRef | null {
  if (!uri.startsWith(SECRET_REF_SCHEME)) {
    return null;
  }

  const rest = uri.slice(SECRET_REF_SCHEME.length);
  const [pathPart, queryPart] = rest.split("?", 2) as [string, string | undefined];

  const slashIndex = pathPart.indexOf("/");
  if (slashIndex === -1) {
    return null;
  }

  const provider = pathPart.slice(0, slashIndex) as SecretRefProvider;
  const validProviders: SecretRefProvider[] = ["control_plane"];
  if (!validProviders.includes(provider)) {
    return null;
  }

  const path = pathPart.slice(slashIndex + 1);
  if (!path) {
    return null;
  }

  const ref: SecretRef = { provider, path };

  if (queryPart) {
    const params = new URLSearchParams(queryPart);
    const version = params.get("version");
    if (version) {
      ref.version = version;
    }
  }

  return ref;
}
export type AgentAccessMode = "all_tenant_users" | "allow_list";
export type GroundingCapabilityKey = "knowledge.query" | "search.query";
export type KnowledgeQueryMode = "answer" | "summarize" | "extract";
export type SearchQueryMode =
  | "company_research"
  | "person_research"
  | "news_lookup"
  | "market_lookup"
  | "website_extract"
  | "source_summarize";
export type AgentThinkingDefault =
  | "off"
  | "minimal"
  | "low"
  | "medium"
  | "high"
  | "xhigh"
  | "adaptive";

// ---------------------------------------------------------------------------
// Grounding policy contracts (P3-001 / P3-002)
// ---------------------------------------------------------------------------

/**
 * Per-mode enablement for a grounding capability.
 *
 * `maxResultsLimit` caps the number of results the runtime will request
 * regardless of what the caller asks for. `allowedChannels`, when non-empty,
 * restricts which channel surfaces may trigger this mode.
 */
export interface GroundingModePolicy {
  enabled: boolean;
  maxResultsLimit?: number;
  allowedChannels?: string[];
}

/**
 * Per-agent grounding policy compiled into the snapshot.
 *
 * Controls which `knowledge.query` and `search.query` modes are available
 * and under what constraints. The runtime must validate every grounding
 * request against this policy before forwarding to the adapter service.
 */
export interface GroundingPolicy {
  knowledge: {
    enabled: boolean;
    modes: Partial<Record<KnowledgeQueryMode, GroundingModePolicy>>;
  };
  search: {
    enabled: boolean;
    modes: Partial<Record<SearchQueryMode, GroundingModePolicy>>;
  };
}

/**
 * Structured error returned when a grounding query is rejected by policy
 * or the adapter service fails.
 */
export interface GroundingQueryError {
  capability: GroundingCapabilityKey;
  code:
    | "grounding.disabled"
    | "grounding.mode_disabled"
    | "grounding.channel_denied"
    | "grounding.agent_not_found"
    | "grounding.no_snapshot"
    | "grounding.no_service"
    | "grounding.adapter_error";
  message: string;
}

export type ChannelType =
  | "mobile"
  | "whatsapp"
  | "slack"
  | "msteams"
  | "telegram"
  | "webchat"
  | "other";

export interface ActorRef {
  actorType: ActorType;
  actorId: string;
  tenantId: TenantId;
  userId?: string;
  displayName?: string;
}

export interface KnowledgeQueryRequest {
  tenantId: TenantId;
  agentId: AgentId;
  conversationId?: ConversationId;
  requestedBy: ActorRef;
  channel?: ChannelType | string;
  mode: KnowledgeQueryMode;
  query: string;
  context?: JsonObject;
  maxResults?: number;
}

export interface KnowledgeQuerySource {
  sourceId: string;
  title: string;
  url?: string;
  excerpt?: string;
  confidence?: number;
  metadata?: JsonObject;
}

export interface KnowledgeQueryResponse {
  capability: "knowledge.query";
  mode: KnowledgeQueryMode;
  query: string;
  grounded: boolean;
  answerability: "certain" | "likely" | "partial" | "unknown";
  answer: string;
  sources: KnowledgeQuerySource[];
  facts?: JsonObject;
  metadata?: JsonObject;
}

export interface TenantUserMapping {
  tenantId: TenantId;
  channel: ChannelType | string;
  userId: string;
  displayName?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  metadata?: JsonObject;
}

export interface TenantAppUserRecord {
  tenantId: TenantId;
  userId: string;
  email: string;
  displayName: string;
  phone?: string;
  roleKeys: TenantAppUserRoleKey[];
  isIdentityActive?: boolean;
  isApplicationAssignmentActive?: boolean;
  phoneConfirmed?: boolean;
  syncedAt: IsoTimestamp;
  createdAt: IsoTimestamp;
  updatedAt: IsoTimestamp;
  deletedAt?: IsoTimestamp;
  metadata?: JsonObject;
}

export interface SearchQueryRequest {
  tenantId: TenantId;
  agentId: AgentId;
  conversationId?: ConversationId;
  requestedBy: ActorRef;
  channel?: ChannelType | string;
  mode: SearchQueryMode;
  query: string;
  context?: JsonObject;
  maxResults?: number;
}

export interface SearchQueryResult {
  resultId: string;
  title: string;
  url?: string;
  excerpt?: string;
  sourceKind: "website" | "document" | "news" | "company_profile" | "person_profile";
  publishedAt?: IsoTimestamp;
  confidence?: number;
  metadata?: JsonObject;
}

export interface SearchQueryResponse {
  capability: "search.query";
  mode: SearchQueryMode;
  query: string;
  grounded: boolean;
  summary?: string;
  results: SearchQueryResult[];
  metadata?: JsonObject;
}

export interface AgentAccessPolicyRecord {
  tenantId: TenantId;
  agentId: AgentId;
  accessMode: AgentAccessMode;
  allowedUserIds?: string[];
  allowedChannels: string[];
  notes?: string;
  metadata?: JsonObject;
}

export interface GeneratedArtifactRef {
  key:
    | "AGENTS.md"
    | "SOUL.md"
    | "TOOLS.md"
    | "IDENTITY.md"
    | "USER.md"
    | "MEMORY_POLICY.md"
    | "CHANNEL_POLICY.md";
  relativePath: string;
  contentHash: HashString;
  generatedAt: IsoTimestamp;
}

export interface AgentPromptArtifactBundle {
  agentId: AgentId;
  generation: SnapshotGeneration;
  snapshotHash: HashString;
  artifacts: GeneratedArtifactRef[];
}

export type AgentModelConfigValue =
  | string
  | {
      primary?: string;
      fallbacks?: string[];
    };

export interface AgentModelEntryRecord {
  alias?: string;
  params?: JsonObject;
  streaming?: boolean;
}

export type ProviderCacheCapability =
  | "explicitStablePrefixCache"
  | "implicitPrefixCache"
  | "noProviderCache";

export type StablePromptCacheStatus = "active" | "stale" | "invalid" | "failed";

export type StablePromptCacheErrorCode =
  | "cache_unsupported_provider"
  | "cache_material_missing"
  | "cache_provider_upsert_failed"
  | "cache_record_invalid";

export interface AgentModelLaneRecord {
  model?: AgentModelConfigValue;
  params?: JsonObject;
}

export interface AgentModelLanesRecord {
  mainTurn?: AgentModelLaneRecord;
  reducer?: AgentModelLaneRecord;
}

export interface AgentModelConfigRecord {
  model?: AgentModelConfigValue;
  imageModel?: AgentModelConfigValue;
  imageGenerationModel?: AgentModelConfigValue;
  pdfModel?: AgentModelConfigValue;
  models?: Record<string, AgentModelEntryRecord>;
  params?: JsonObject;
  /**
   * Explicit model lanes for agent orchestration.
   *
   * `model` remains the legacy main-turn selector. New work should prefer
   * `lanes.mainTurn.model` and `lanes.reducer.model` so the runtime can route
   * user turns and reducer/summary work independently.
   */
  lanes?: AgentModelLanesRecord;
  compaction?: {
    model?: string;
  };
  heartbeat?: {
    model?: string;
  };
  subagents?: {
    model?: AgentModelConfigValue;
  };
}

export interface AgentRuntimePolicy {
  thinkingDefault?: AgentThinkingDefault;
  allowUserThinkingOverride?: boolean;
  executionClass: ExecutionClass;
  enabledWorkflowKeys: string[];
  enabledConnectorKeys: string[];
  allowedToolNames: string[];
  runtimeConnectorAccess?: AgentRuntimeConnectorAccessPolicy;
  dangerousToolDeny: string[];
  allowCrossConversationSummaryAccess: boolean;
  memoryPolicy: JsonObject;
  responseStyle: JsonObject;
  groundingPolicy?: GroundingPolicy;
  modelConfig?: AgentModelConfigRecord;
}

export const DEFAULT_STRICT_MESSAGING_TOOL_ALLOWLIST = [
  "message",
  "sessions_list",
  "sessions_history",
  "sessions_send",
  "session_status",
] as const;

export const DEFAULT_AGENT_TOOL_ALLOWLIST = [
  ...DEFAULT_STRICT_MESSAGING_TOOL_ALLOWLIST,
  "memory_search",
  "memory_get",
] as const;

export const DEFAULT_AGENT_MEMORY_POLICY = {
  enabled: true,
  sessionSummary: true,
  tenantKnowledge: true,
  sessionMemory: {
    enabled: true,
    messages: 15,
    llmSlug: false,
  },
  memoryFlush: {
    enabled: true,
  },
  dreaming: {
    enabled: false,
    frequency: "0 3 * * *",
  },
  activeMemory: {
    enabled: true,
    allowedChatTypes: ["direct", "explicit"],
    queryMode: "recent",
    promptStyle: "balanced",
    timeoutMs: 15000,
  },
  memorySearch: {
    sources: ["memory"],
    store: {
      vector: {
        enabled: true,
      },
    },
  },
} as const satisfies JsonObject;

export interface AgentRuntimeConnectorAccessPolicy {
  allowedBindingIds: string[];
  allowedTargetKeysByBindingId: Record<string, string[]>;
  defaultTargetKeyByBindingId?: Record<string, string>;
}

export interface AgentRuntimeRecord {
  agentId: AgentId;
  name: string;
  description?: string;
  personaMarkdown: string;
  instructionsMarkdown: string;
  identityMarkdown?: string;
  userMarkdown?: string;
  channelOverlays?: Record<string, JsonObject>;
  skillsSnapshot: RuntimeSkillSnapshot;
  runtimePolicy: AgentRuntimePolicy;
  artifactBundle: AgentPromptArtifactBundle;
}

export interface RuntimeSkillSnapshot {
  prompt: string;
  skills: Array<{ name: string; primaryEnv?: string; requiredEnv?: string[] }>;
  resolvedSkills: Array<{
    name: string;
    displayName?: string;
    description?: string;
    version?: number;
    contentHash?: string;
    files?: RuntimeSkillFile[];
  }>;
  skillFilter?: string[];
  version: number;
}

export interface RuntimeSkillFile {
  relativePath: string;
  content: string;
  contentType: string;
}

export interface StablePromptCacheIdentity {
  tenantId: TenantId;
  agentId: AgentId;
  provider: string;
  modelId: string;
  promptSchemaVersion: string;
  /** Invalidate when stable system rules change. */
  systemRulesHash: HashString;
  /** Invalidate when the exposed stable tool surface changes. */
  toolSurfaceHash: HashString;
  /** Invalidate when the compiled tenant/runtime snapshot changes. */
  snapshotHash: HashString;
  /** Invalidate when the channel class changes (for example `mobile_dm` vs `slack_group`). */
  channelClass: string;
  /** Invalidate when provider cache semantics change for the same model/provider pair. */
  providerCacheCapability: ProviderCacheCapability;
}

export interface StablePromptCacheRecord extends StablePromptCacheIdentity {
  cacheKey: HashString;
  providerCacheHandle?: JsonValue;
  stablePrefixHash: HashString;
  stablePrefixTokenEstimate?: number;
  createdAt?: IsoTimestamp;
  lastValidatedAt?: IsoTimestamp;
  expiresAt?: IsoTimestamp;
  status: StablePromptCacheStatus;
  failureReason?: StablePromptCacheErrorCode | string;
}

/**
 * Canonical ordered key material used for stable prompt cache identity hashing.
 *
 * The caller is responsible for applying the chosen hash function to this
 * byte-stable joined string. Dynamic-tail blocks such as session frame,
 * rolling summary, latest user turn, and current observation must never be
 * added here because they are intentionally excluded from stable-prefix cache
 * invalidation.
 */
export function buildStablePromptCacheKeyMaterial(identity: StablePromptCacheIdentity): string {
  return [
    identity.tenantId,
    identity.agentId,
    identity.provider,
    identity.modelId,
    identity.promptSchemaVersion,
    identity.systemRulesHash,
    identity.toolSurfaceHash,
    identity.snapshotHash,
    identity.channelClass,
    identity.providerCacheCapability,
  ].join("\n");
}

export interface ChannelConnectionRecord {
  id: string;
  tenantId: TenantId;
  channelType: ChannelType | string;
  displayName: string;
  status: "draft" | "active" | "paused" | "error";
  credentialsRef: SecretRef;
  config: JsonObject;
}

export type TenantAiProviderConnectionAuthMode = "api_key" | "oauth";
export type TenantAiProviderConnectionStatus = "active" | "error" | "disconnected";

export interface TenantAiProviderConnectionRecord {
  id: string;
  tenantId: TenantId;
  providerId: string;
  providerGroupId: string;
  label: string;
  authMode: TenantAiProviderConnectionAuthMode;
  status: TenantAiProviderConnectionStatus;
  credentialsRef: SecretRef;
  profileId: string;
  metadata?: JsonObject;
  lastValidatedAt?: IsoTimestamp;
  lastError?: string;
}

export interface ChannelDeploymentRecord {
  id: string;
  tenantId: TenantId;
  agentId: AgentId;
  channelConnectionId: string;
  allowDms: boolean;
  allowGroups: boolean;
  requireMention: boolean;
  brandingOverlay?: JsonObject;
  status: "active" | "paused";
}

export interface ConnectorInstallRecord {
  id: string;
  tenantId: TenantId;
  connectorKey: string;
  status: "active" | "paused" | "error";
  credentialsRef: SecretRef;
  config: JsonObject;
}

export type CapabilityExecutionHost = "control_plane_app" | "runtime_connector";

export type ProviderDescriptorKind = "first_party_app" | "runtime_connector";
export type ProviderDescriptorFieldType = "string" | "secret_ref" | "json" | "select";
export type ProviderDescriptorOperationType = "read" | "write";

export interface ProviderDescriptorFieldOption {
  value: string;
  label?: string;
  description?: string;
}

export interface ProviderDescriptorField {
  key: string;
  label: string;
  type: ProviderDescriptorFieldType;
  description?: string;
  placeholder?: string;
  required?: boolean;
  defaultValue?: JsonValue;
  options?: ProviderDescriptorFieldOption[];
}

export interface ProviderDescriptorInstallSurface {
  fields: ProviderDescriptorField[];
}

export interface ProviderDescriptorTargetSurface {
  fields: ProviderDescriptorField[];
}

export interface ProviderDescriptorActivation {
  tenantScoped: boolean;
  requiresConnectorInstall: boolean;
  requiresBinding: boolean;
  supportsNamedTargets: boolean;
  supportedCredentialModes?: ConnectorCredentialMode[];
}

export interface ProviderDescriptorOperation {
  operationKey: string;
  operationType?: ProviderDescriptorOperationType;
  description?: string;
  inputSchema?: JsonObject;
  outputSchema?: JsonObject;
}

export interface ProviderDescriptorCapability {
  capabilityKey: string;
  name?: string;
  description?: string;
  operations: ProviderDescriptorOperation[];
}

export interface ProviderDescriptorRecord {
  contractVersion?: string;
  descriptorKey: string;
  providerKey: string;
  displayName: string;
  description?: string;
  kind: ProviderDescriptorKind;
  executionHost: CapabilityExecutionHost;
  appKey?: string;
  connectorKey?: string;
  relatedConnectorKeys?: string[];
  activation: ProviderDescriptorActivation;
  capabilities?: ProviderDescriptorCapability[];
  install?: ProviderDescriptorInstallSurface;
  targets?: ProviderDescriptorTargetSurface;
  metadata?: JsonObject;
}

export function listProviderCapabilityKeys(record: ProviderDescriptorRecord): string[] {
  return Array.from(
    new Set(
      (record.capabilities ?? [])
        .map((entry) => entry.capabilityKey.trim())
        .filter((entry) => entry.length > 0),
    ),
  ).toSorted((left, right) => left.localeCompare(right));
}

export function inferProviderDescriptorOperationType(
  operationKey: string,
): ProviderDescriptorOperationType {
  const normalized = operationKey.trim().toLowerCase();
  if (
    normalized.startsWith("create") ||
    normalized.startsWith("update") ||
    normalized.startsWith("delete") ||
    normalized.startsWith("send") ||
    normalized.startsWith("write")
  ) {
    return "write";
  }
  return "read";
}

export interface TenantCapabilityBindingRecord {
  id: string;
  tenantId: TenantId;
  capabilityKey: string;
  descriptorKey?: string;
  executionHost?: CapabilityExecutionHost;
  connectorInstallId?: string;
  providerKey: string;
  credentialMode: ConnectorCredentialMode;
  requiredAssuranceLevel: ConnectorAssuranceLevel;
  status: "active" | "paused" | "error";
  defaultTargetKey?: string;
  policy?: JsonObject;
  defaults?: JsonObject;
  permissions?: JsonObject;
  updatedAt: IsoTimestamp;
}

export interface TenantCapabilityTargetRecord {
  id: string;
  tenantId: TenantId;
  bindingId: string;
  targetKey: string;
  displayName: string;
  providerTargetRef: string;
  status: "active" | "paused";
  metadata?: JsonObject;
  defaultAssigneeIdentityId?: string;
  attachmentPolicy?: JsonObject;
  schedulePolicy?: JsonObject;
}

export type ProviderDelegatedAuthState = "ready" | "pending" | "expired" | "missing";

export interface ProviderIdentityLinkRecord {
  tenantId: TenantId;
  providerKey: string;
  userId: string;
  providerUserRef: string;
  delegatedAuthState: ProviderDelegatedAuthState;
  lastVerifiedAt?: IsoTimestamp;
  metadata?: JsonObject;
}

export interface ConnectorActionDefinition {
  connectorKey: string;
  actionKey: string;
  label: string;
  description: string;
  kind: ConnectorActionKind;
  inputSchema?: JsonObject;
  outputSchema?: JsonObject;
  idempotencyStrategy: string;
  allowedExecutionClasses: ExecutionClass[];
  auditTemplate: JsonObject;
}

export interface WorkflowDefinition {
  workflowKey: string;
  name: string;
  description: string;
  version: number;
  inputSchema: JsonObject;
  outputSchema: JsonObject;
  sideEffectClass: "none" | "read_only" | "write";
  stepSpec: JsonObject;
}

export interface WorkflowPolicyRecord {
  tenantId: TenantId;
  workflowKey: string;
  enabled: boolean;
  allowedChannels: string[];
  allowedRoles: string[];
  config?: JsonObject;
}

export interface WorkflowEngineConfig {
  kind: WorkflowEngineKind;
  config?: JsonObject;
}

export interface TenantRuntimeToolSearchPolicy {
  enabled: boolean;
  mode: "tools";
}

export interface TenantRuntimeToolsPolicy {
  toolSearch: TenantRuntimeToolSearchPolicy;
}

export interface TenantRuntimePolicies {
  dmScope: "per-channel-peer" | "per-account-channel-peer";
  executionClassDefaults: Record<string, ExecutionClass>;
  sandboxDefaults: {
    mode: "off" | "non-main" | "all";
    scope: "session" | "agent" | "shared";
    workspaceAccess: "none" | "ro" | "rw";
  };
  dangerousToolDeny: string[];
  customerCommandPolicy: {
    commandsEnabled: false;
    allowSafeUxCommands: boolean;
    allowedSafeCommands?: string[];
  };
  auditPolicy: JsonObject;
  workflowEngine: WorkflowEngineConfig;
  tools: TenantRuntimeToolsPolicy;
}

export interface TenantRuntimeSnapshot {
  tenantId: TenantId;
  generatedAt: IsoTimestamp;
  snapshotHash: HashString;
  generation: SnapshotGeneration;
  agents: AgentRuntimeRecord[];
  agentAccessPolicies: AgentAccessPolicyRecord[];
  channelConnections: ChannelConnectionRecord[];
  channelDeployments: ChannelDeploymentRecord[];
  connectorInstalls: ConnectorInstallRecord[];
  tenantAppUsers?: TenantAppUserRecord[];
  lightweightAutomations?: LightweightAutomationRecord[];
  tenantAiProviderConnections?: TenantAiProviderConnectionRecord[];
  tenantCapabilityBindings?: TenantCapabilityBindingRecord[];
  tenantCapabilityTargets?: TenantCapabilityTargetRecord[];
  providerIdentityLinks?: ProviderIdentityLinkRecord[];
  workflowPolicies: WorkflowPolicyRecord[];
  workflowDefinitions: WorkflowDefinition[];
  identityRules: TenantUserMapping[];
  runtimePolicies: TenantRuntimePolicies;
}

export interface RuntimeHello {
  type: "runtime.hello";
  tenantId: TenantId;
  runtimeKey: RuntimeKey;
  currentSnapshotHash?: HashString;
  currentGeneration?: SnapshotGeneration;
  runtimeVersion: string;
  capabilities: {
    channels: string[];
    workflowEngines: WorkflowEngineKind[];
    connectorKeys?: string[];
  };
}

export interface RuntimeApply {
  type: "runtime.apply";
  tenantId: TenantId;
  generation: SnapshotGeneration;
  snapshotHash: HashString;
  snapshot: TenantRuntimeSnapshot;
  reason: "operator_save" | "manual_reapply" | "reconcile";
  requestedBy: ActorRef;
  requestedAt: IsoTimestamp;
}

export interface RuntimeApplyAck {
  type: "runtime.apply_ack";
  tenantId: TenantId;
  runtimeKey: RuntimeKey;
  generation: SnapshotGeneration;
  snapshotHash: HashString;
  status: "applied";
  appliedAt: IsoTimestamp;
}

export function isAgentAccessAllowed(params: {
  snapshot: Pick<TenantRuntimeSnapshot, "agentAccessPolicies">;
  agentId: AgentId;
  userId?: string;
  channel?: ChannelType | string;
}): boolean {
  const policy = params.snapshot.agentAccessPolicies.find(
    (entry) => entry.agentId === params.agentId,
  );
  if (!policy) {
    return true;
  }

  if (policy.allowedChannels.length > 0) {
    if (!params.channel || !policy.allowedChannels.includes(params.channel)) {
      return false;
    }
  }

  if (policy.accessMode === "all_tenant_users") {
    return true;
  }

  const actorId = params.userId;
  if (!actorId) {
    return false;
  }

  const allowedActorIds = policy.allowedUserIds ?? [];
  return allowedActorIds.includes(actorId);
}

export function isRuntimeConnectorBindingAllowed(params: {
  runtimePolicy: Pick<AgentRuntimePolicy, "runtimeConnectorAccess">;
  bindingId: string;
}): boolean {
  const accessPolicy = params.runtimePolicy.runtimeConnectorAccess;
  if (!accessPolicy) {
    return true;
  }
  return accessPolicy.allowedBindingIds.includes(params.bindingId);
}

export function listRuntimeConnectorAllowedTargetKeys(params: {
  runtimePolicy: Pick<AgentRuntimePolicy, "runtimeConnectorAccess">;
  bindingId: string;
}): string[] | null {
  const accessPolicy = params.runtimePolicy.runtimeConnectorAccess;
  if (!accessPolicy) {
    return null;
  }
  if (
    !Object.prototype.hasOwnProperty.call(
      accessPolicy.allowedTargetKeysByBindingId,
      params.bindingId,
    )
  ) {
    return null;
  }
  return accessPolicy.allowedTargetKeysByBindingId[params.bindingId] ?? [];
}

export function isRuntimeConnectorTargetAllowed(params: {
  runtimePolicy: Pick<AgentRuntimePolicy, "runtimeConnectorAccess">;
  bindingId: string;
  targetKey: string;
}): boolean {
  const allowedTargetKeys = listRuntimeConnectorAllowedTargetKeys(params);
  if (!allowedTargetKeys) {
    return true;
  }
  return allowedTargetKeys.includes(params.targetKey);
}

export function resolveRuntimeConnectorDefaultTarget(params: {
  runtimePolicy: Pick<AgentRuntimePolicy, "runtimeConnectorAccess">;
  bindingId: string;
}): string | undefined {
  const accessPolicy = params.runtimePolicy.runtimeConnectorAccess;
  if (!accessPolicy) {
    return undefined;
  }
  const targetKey = accessPolicy.defaultTargetKeyByBindingId?.[params.bindingId];
  return typeof targetKey === "string" && targetKey.trim() ? targetKey.trim() : undefined;
}

/**
 * Validate a grounding query against the agent's compiled grounding policy.
 *
 * Returns `null` when the request is allowed, or a `GroundingQueryError`
 * describing why it was rejected.
 */
export function validateGroundingRequest(params: {
  capability: GroundingCapabilityKey;
  mode: string;
  channel?: string;
  groundingPolicy?: GroundingPolicy;
}): GroundingQueryError | null {
  const { capability, mode, channel, groundingPolicy } = params;

  if (!groundingPolicy) {
    return {
      capability,
      code: "grounding.disabled",
      message: `Agent has no grounding policy configured.`,
    };
  }

  const section =
    capability === "knowledge.query" ? groundingPolicy.knowledge : groundingPolicy.search;

  if (!section.enabled) {
    return {
      capability,
      code: "grounding.disabled",
      message: `Grounding capability ${capability} is disabled for this agent.`,
    };
  }

  const modePolicy = section.modes[mode as keyof typeof section.modes] as
    | GroundingModePolicy
    | undefined;

  if (!modePolicy || !modePolicy.enabled) {
    return {
      capability,
      code: "grounding.mode_disabled",
      message: `Mode "${mode}" is disabled for capability ${capability}.`,
    };
  }

  if (
    modePolicy.allowedChannels &&
    modePolicy.allowedChannels.length > 0 &&
    channel &&
    !modePolicy.allowedChannels.includes(channel)
  ) {
    return {
      capability,
      code: "grounding.channel_denied",
      message: `Channel "${channel}" is not allowed for ${capability} mode "${mode}".`,
    };
  }

  return null;
}

/**
 * Resolve the effective `maxResults` for a grounding request, capping at the
 * policy limit when one is set.
 */
export function resolveGroundingMaxResults(params: {
  capability: GroundingCapabilityKey;
  mode: string;
  requestedMax?: number;
  groundingPolicy?: GroundingPolicy;
}): number | undefined {
  const { capability, mode, requestedMax, groundingPolicy } = params;
  if (!groundingPolicy) {
    return requestedMax;
  }

  const section =
    capability === "knowledge.query" ? groundingPolicy.knowledge : groundingPolicy.search;
  const modePolicy = section.modes[mode as keyof typeof section.modes] as
    | GroundingModePolicy
    | undefined;

  if (!modePolicy?.maxResultsLimit) {
    return requestedMax;
  }
  if (requestedMax === undefined) {
    return modePolicy.maxResultsLimit;
  }
  return Math.min(requestedMax, modePolicy.maxResultsLimit);
}

function connectorAssuranceLevelRank(level: ConnectorAssuranceLevel): number {
  switch (level) {
    case "level_0":
      return 0;
    case "level_1":
      return 1;
    case "level_2":
      return 2;
  }
}

export interface RuntimeApplyNack {
  type: "runtime.apply_nack";
  tenantId: TenantId;
  runtimeKey: RuntimeKey;
  generation: SnapshotGeneration;
  snapshotHash: HashString;
  status: "rejected";
  errorCode: string;
  errorMessage: string;
  failedAt: IsoTimestamp;
}

export type RuntimeIssueSeverity = "error" | "warning";

export interface RuntimeBootstrapIssue {
  severity: RuntimeIssueSeverity;
  code: string;
  message: string;
  path?: string;
}

export type RuntimeChannelHealthReason =
  | "healthy"
  | "unmanaged"
  | "not-running"
  | "busy"
  | "stuck"
  | "startup-connect-grace"
  | "disconnected"
  | "stale-socket";

export interface RuntimeChannelHealthRecord {
  channelId: string;
  accountId: string;
  healthy: boolean;
  reason: RuntimeChannelHealthReason;
}

export interface RuntimeStateReport {
  type: "runtime.state_report";
  tenantId: TenantId;
  runtimeKey: RuntimeKey;
  status: "healthy" | "degraded" | "error";
  currentSnapshotHash?: HashString;
  currentGeneration?: SnapshotGeneration;
  issues?: RuntimeBootstrapIssue[];
  channelHealth?: RuntimeChannelHealthRecord[];
  lastSeenAt: IsoTimestamp;
}

export interface TurnStartRequest {
  tenantId: TenantId;
  sessionId: ConversationId;
  channel: ChannelType | string;
  accountId?: string;
  agentId?: AgentId;
  cronJobId?: string;
  senderId?: string;
  senderE164?: string;
  messageId?: string;
  messageText?: string;
}

export interface TurnActor {
  userId: string;
  source: "identity_rule" | "provider_identity_link" | "tenant_user";
}

export interface TurnSession {
  id: ConversationId;
  agentId: AgentId;
}

export interface TurnPrompt {
  addition: string;
}

export type TurnSkillSnapshot = RuntimeSkillSnapshot;

export interface TurnTool {
  name: string;
  kind: "connector_capability" | "workflow";
  label?: string;
  description?: string;
  inputSchema?: JsonObject;
  capabilityKey?: string;
  operationKey?: string;
  operationType?: ProviderDescriptorOperationType;
  connectorKey?: string;
  providerKey?: string;
  bindingIds?: string[];
  defaultBindingId?: string;
  targetKeys?: string[];
  defaultTargetKey?: string;
  targetRequired?: boolean;
  outputSchema?: JsonObject;
}

export interface TurnResponse {
  tenantId: TenantId;
  generation: SnapshotGeneration;
  snapshotHash: HashString;
  promptEpochHash: HashString;
  channel?: string;
  cronJobId?: string;
  session: TurnSession;
  actor?: TurnActor;
  prompt: TurnPrompt;
  tools: TurnTool[];
  skillsSnapshot: TurnSkillSnapshot;
}

export interface McpResourceTokenRequest {
  tenantId: TenantId;
  connectorInstallId: string;
  capabilityKey: string;
  operationKey: string;
  userId: string;
  agentId?: AgentId;
  sessionId?: ConversationId;
  channel?: string;
}

export interface McpResourceTokenResponse {
  accessToken: string;
  tokenType: "Bearer" | string;
  expiresIn: number;
  audience: string;
  scopes: string[];
}

export type RuntimeControlMessage =
  | RuntimeHello
  | RuntimeApply
  | RuntimeApplyAck
  | RuntimeApplyNack
  | RuntimeStateReport;

export interface ConversationRecord {
  conversationId: ConversationId;
  tenantId: TenantId;
  agentId: AgentId;
  channelDeploymentId: string;
  sessionKey: string;
  externalPeerId: string;
  userId?: string;
  startedAt: IsoTimestamp;
  updatedAt: IsoTimestamp;
}

export interface ConversationSummaryRecord {
  id: string;
  tenantId: TenantId;
  conversationId: ConversationId;
  summaryKind: "rolling" | "final" | "workflow_outcome";
  summaryText: string;
  metadata?: JsonObject;
  createdAt: IsoTimestamp;
}

export type MobileUiSurface = "conversation" | "approval" | "details" | "collection" | "form";
export type MobileUiStatusTone = "neutral" | "info" | "success" | "warning" | "danger";
export type MobileUiActionKind =
  | "submit"
  | "cancel"
  | "approve"
  | "reject"
  | "open"
  | "refresh"
  | "custom";
export type MobileUiActionStyle = "primary" | "secondary" | "success" | "danger";
export type MobileUiTableColumnWidth = "compact" | "normal" | "wide";
export type MobileUiAlignment = "start" | "center" | "end";
export type MobileUiSelectionMode = "single" | "multiple";
export type MobileUiFormFieldKind =
  | "text"
  | "textarea"
  | "email"
  | "number"
  | "date"
  | "select"
  | "checkbox";

export interface MobileUiTargetRef {
  kind: string;
  id: string;
  label?: string;
  metadata?: JsonObject;
}

export interface MobileUiActionCallback {
  actionId: string;
  workflowKey?: string;
  executionId?: WorkflowExecutionId;
  target?: MobileUiTargetRef;
  payload?: JsonObject;
}

export interface MobileUiInboundAction {
  kind: "mobile.action";
  actionId: string;
  action: MobileUiActionKind;
  workflowKey?: string;
  executionId?: WorkflowExecutionId;
  target?: MobileUiTargetRef;
  payload?: JsonObject;
}

export interface MobileUiAction {
  id: string;
  label: string;
  kind: MobileUiActionKind;
  style?: MobileUiActionStyle;
  disabled?: boolean;
  callback: MobileUiActionCallback;
}

export interface MobileUiStatusBadge {
  label: string;
  tone: MobileUiStatusTone;
}

export interface MobileUiTextBlock {
  type: "text";
  text: string;
}

export interface MobileUiFieldItem {
  key: string;
  label: string;
  value: string;
  rawValue?: JsonValue;
  badge?: MobileUiStatusBadge;
}

export interface MobileUiFieldListBlock {
  type: "field_list";
  title?: string;
  fields: MobileUiFieldItem[];
}

export interface MobileUiTableColumn {
  key: string;
  label: string;
  width?: MobileUiTableColumnWidth;
  align?: MobileUiAlignment;
}

export interface MobileUiTableCell {
  text: string;
  rawValue?: JsonValue;
  badge?: MobileUiStatusBadge;
}

export interface MobileUiTableRow {
  id: string;
  cells: Record<string, MobileUiTableCell>;
  actions?: MobileUiAction[];
}

export interface MobileUiTableBlock {
  type: "table";
  title?: string;
  columns: MobileUiTableColumn[];
  rows: MobileUiTableRow[];
}

export interface MobileUiChoice {
  id: string;
  label: string;
  description?: string;
  value: JsonValue;
}

export interface MobileUiChoiceSetBlock {
  type: "choice_set";
  title?: string;
  selectionMode: MobileUiSelectionMode;
  choices: MobileUiChoice[];
}

export interface MobileUiFormFieldOption {
  label: string;
  value: string;
}

export interface MobileUiFormField {
  key: string;
  label: string;
  kind: MobileUiFormFieldKind;
  required?: boolean;
  placeholder?: string;
  helpText?: string;
  value?: JsonValue;
  options?: MobileUiFormFieldOption[];
}

export interface MobileUiFormBlock {
  type: "form";
  title?: string;
  fields: MobileUiFormField[];
  submitAction?: MobileUiAction;
  cancelAction?: MobileUiAction;
}

export interface MobileUiConfirmationBlock {
  type: "confirmation";
  title: string;
  prompt: string;
  tone?: MobileUiStatusTone;
  fields?: MobileUiFieldItem[];
  actions: MobileUiAction[];
}

export interface MobileUiTimelineEvent {
  id: string;
  title: string;
  timestamp?: IsoTimestamp;
  description?: string;
  badge?: MobileUiStatusBadge;
}

export interface MobileUiTimelineBlock {
  type: "timeline";
  title?: string;
  events: MobileUiTimelineEvent[];
}

export interface MobileUiCardBlock {
  type: "card";
  id?: string;
  title: string;
  subtitle?: string;
  badge?: MobileUiStatusBadge;
  blocks?: MobileUiBlock[];
  actions?: MobileUiAction[];
}

export type MobileUiBlock =
  | MobileUiCardBlock
  | MobileUiChoiceSetBlock
  | MobileUiConfirmationBlock
  | MobileUiFieldListBlock
  | MobileUiFormBlock
  | MobileUiTableBlock
  | MobileUiTextBlock
  | MobileUiTimelineBlock;

export interface MobileUiEnvelope {
  schemaVersion: 1;
  surface: MobileUiSurface;
  title?: string;
  summary?: string;
  blocks: MobileUiBlock[];
  actions?: MobileUiAction[];
  clientHints?: JsonObject;
}

export interface MobileBootstrapRequest {}

export interface MobileAuthenticatedUser {
  userId: string;
  displayName?: string;
  email?: string;
}

export interface MobileTenantSummary {
  tenantId: TenantId;
  name: string;
  slug?: string;
}

export interface MobileVisibleAgentRecord {
  agentId: AgentId;
  name: string;
  description?: string;
  sessionId: ConversationId;
  deploymentId: string;
  brandingOverlay?: JsonObject;
}

export interface SessionSummary {
  sessionId: ConversationId;
  tenantId: TenantId;
  userId: string;
  agentId: AgentId;
  agentName: string;
  channel: ChannelType;
  messageCount: number;
  title: string;
  lastMessageText?: string;
  lastMessageAt?: IsoTimestamp;
  createdAt: IsoTimestamp;
  updatedAt: IsoTimestamp;
  surfaces?: ChannelType[];
}

export interface SessionMessage {
  messageId: string;
  tenantId: TenantId;
  sessionId: ConversationId;
  userId: string;
  agentId: AgentId;
  channel: ChannelType;
  role: "user" | "assistant" | "system";
  direction: "inbound" | "outbound";
  text?: string;
  mediaUrls?: string[];
  content?: SessionMessageContentBlock[];
  channelData?: JsonObject;
  deliveryMetadata?: JsonObject;
  createdAt: IsoTimestamp;
}

export type SessionMessageContentBlock = JsonObject & {
  type: string;
};

export const MOBILE_RELAY_CHANNEL_DATA_KEY = "openclawMobileRelay";

export interface MobileRelayChannelData {
  tenantId: TenantId;
  sessionId: ConversationId;
  userId: string;
  agentId: AgentId;
  agentName: string;
}

export interface AuditEventRecord {
  id: string;
  tenantId: TenantId;
  actor: ActorRef;
  resourceType: string;
  resourceId: string;
  eventType: string;
  details: JsonObject;
  createdAt: IsoTimestamp;
}

export interface ActionRequest {
  tenantId: TenantId;
  connectorKey: string;
  actionKey: string;
  requestedBy: ActorRef;
  conversationId: ConversationId;
  channel: ChannelType | string;
  executionClass: ExecutionClass;
  payload: JsonObject;
  idempotencyKey: IdempotencyKey;
}

export interface ActionResult {
  ok: boolean;
  connectorKey: string;
  actionKey: string;
  result: JsonValue;
  externalIds?: Record<string, string>;
  summary: string;
  auditData: JsonObject;
  retryable: boolean;
}

export interface WorkflowRequest {
  tenantId: TenantId;
  workflowKey: string;
  requestedBy: ActorRef;
  conversationId: ConversationId;
  channel: ChannelType | string;
  accountId?: string;
  agentId?: AgentId;
  userId?: string;
  input: JsonObject;
  inputSummary: string;
  executionClass: ExecutionClass;
}

export interface WorkflowExecutionRecord {
  executionId: WorkflowExecutionId;
  tenantId: TenantId;
  workflowKey: string;
  requestHash: HashString;
  engineKind: WorkflowEngineKind;
  status: WorkflowExecutionStatus;
  conversationId: ConversationId;
  input: JsonObject;
  result?: JsonValue;
  startedAt: IsoTimestamp;
  completedAt?: IsoTimestamp;
  auditTrail: AuditEventRecord[];
}

export interface WorkflowEngineSubmitResult {
  execution: WorkflowExecutionRecord;
}

export interface WorkflowEngine {
  readonly kind: WorkflowEngineKind;
  submit(request: WorkflowRequest): Promise<WorkflowEngineSubmitResult>;
  getStatus(executionId: WorkflowExecutionId): Promise<WorkflowExecutionRecord | null>;
}

// ============================================================================
// Example payloads
// ============================================================================

export const exampleWorkflowRequest: WorkflowRequest = {
  tenantId: "tenant_acme",
  workflowKey: "workflow.automation.run",
  requestedBy: {
    actorType: "runtime",
    actorId: "runtime",
    tenantId: "tenant_acme",
    displayName: "Runtime",
  },
  conversationId: "cron:daily-digest",
  channel: "cron",
  accountId: "default",
  agentId: "assistant",
  input: {
    automationId: "automation_daily_digest",
    runId: "cron:automation_daily_digest",
  },
  inputSummary: "Run the scheduled daily digest automation.",
  executionClass: "approved_actions",
};

export const exampleWorkflowExecution: WorkflowExecutionRecord = {
  executionId: "wfexec_001",
  tenantId: "tenant_acme",
  workflowKey: "workflow.automation.run",
  requestHash: "sha256:workflow-request-hash",
  engineKind: "local",
  status: "succeeded",
  conversationId: "cron:daily-digest",
  input: exampleWorkflowRequest.input,
  startedAt: "2026-03-21T14:00:00Z",
  completedAt: "2026-03-21T14:00:05Z",
  auditTrail: [],
};

export const exampleMobileCollectionView: MobileUiEnvelope = {
  schemaVersion: 1,
  surface: "collection",
  title: "Open Work Items",
  summary: "A generic table that can represent tasks, tickets, leads, or any work queue.",
  blocks: [
    {
      type: "card",
      title: "Work Queue",
      subtitle: "Items assigned to Alice Example",
      badge: {
        label: "3 open",
        tone: "info",
      },
      blocks: [
        {
          type: "table",
          columns: [
            { key: "title", label: "Title", width: "wide" },
            { key: "owner", label: "Owner" },
            { key: "status", label: "Status", width: "compact" },
          ],
          rows: [
            {
              id: "work_item_001",
              cells: {
                title: { text: "Follow up with Acme" },
                owner: { text: "Alice Example" },
                status: {
                  text: "Open",
                  badge: { label: "Open", tone: "warning" },
                },
              },
              actions: [
                {
                  id: "open-work-item-001",
                  label: "Open",
                  kind: "open",
                  callback: {
                    actionId: "open-work-item-001",
                    target: {
                      kind: "work_item",
                      id: "work_item_001",
                      label: "Follow up with Acme",
                    },
                  },
                },
              ],
            },
          ],
        },
      ],
      actions: [
        {
          id: "refresh-work-queue",
          label: "Refresh",
          kind: "refresh",
          callback: {
            actionId: "refresh-work-queue",
            payload: { scope: "assigned_to_me" },
          },
        },
      ],
    },
  ],
};

export const exampleTenantRuntimeSnapshot: TenantRuntimeSnapshot = {
  tenantId: "tenant_acme",
  generatedAt: "2026-03-21T14:00:00Z",
  snapshotHash: "sha256:snapshot-hash",
  generation: 12,
  agents: [
    {
      agentId: "sales_assistant",
      name: "Assistant",
      description: "Tenant assistant",
      personaMarkdown: "You are the tenant assistant.",
      instructionsMarkdown:
        "Answer clearly. Use workflows for user actions. Never mutate runtime settings.",
      runtimePolicy: {
        executionClass: "approved_actions",
        enabledWorkflowKeys: ["workflow.automation.run"],
        enabledConnectorKeys: ["graphrag"],
        allowedToolNames: [
          "message",
          "sessions_list",
          "sessions_history",
          "sessions_send",
          "session_status",
          "web_search",
          "memory_search",
        ],
        dangerousToolDeny: [
          "exec",
          "bash",
          "process",
          "gateway",
          "cron",
          "apply_patch",
          "write",
          "edit",
        ],
        allowCrossConversationSummaryAccess: false,
        memoryPolicy: { sessionSummary: true, tenantKnowledge: true },
        responseStyle: { tone: "clear", brevity: "medium" },
        groundingPolicy: {
          knowledge: {
            enabled: true,
            modes: {
              answer: { enabled: true, maxResultsLimit: 5 },
              summarize: { enabled: true },
              extract: { enabled: false },
            },
          },
          search: {
            enabled: true,
            modes: {
              company_research: { enabled: true, maxResultsLimit: 10 },
              person_research: { enabled: true, maxResultsLimit: 10 },
              news_lookup: { enabled: true, maxResultsLimit: 5 },
              market_lookup: { enabled: true, maxResultsLimit: 5 },
              website_extract: { enabled: false },
              source_summarize: { enabled: true, allowedChannels: ["mobile"] },
            },
          },
        },
      },
      skillsSnapshot: {
        prompt: "",
        skills: [],
        resolvedSkills: [],
        version: 0,
      },
      artifactBundle: {
        agentId: "sales_assistant",
        generation: 12,
        snapshotHash: "sha256:snapshot-hash",
        artifacts: [
          {
            key: "AGENTS.md",
            relativePath: "agents/sales_assistant/generated/AGENTS.md",
            contentHash: "sha256:agents-md",
            generatedAt: "2026-03-21T14:00:00Z",
          },
          {
            key: "SOUL.md",
            relativePath: "agents/sales_assistant/generated/SOUL.md",
            contentHash: "sha256:soul-md",
            generatedAt: "2026-03-21T14:00:00Z",
          },
        ],
      },
    },
  ],
  agentAccessPolicies: [
    {
      tenantId: "tenant_acme",
      agentId: "sales_assistant",
      accessMode: "allow_list",
      allowedUserIds: ["user_123"],
      allowedChannels: ["mobile"],
      notes: "Pilot users only.",
    },
  ],
  channelConnections: [
    {
      id: "channel_conn_mobile",
      tenantId: "tenant_acme",
      channelType: "mobile",
      displayName: "Acme Mobile App",
      status: "active",
      credentialsRef: createSecretRef("control_plane", "tenant_acme/mobile-app"),
      config: { pushEnabled: true },
    },
  ],
  channelDeployments: [
    {
      id: "channel_dep_mobile_sales",
      tenantId: "tenant_acme",
      agentId: "sales_assistant",
      channelConnectionId: "channel_conn_mobile",
      allowDms: true,
      allowGroups: false,
      requireMention: false,
      status: "active",
    },
  ],
  connectorInstalls: [
    {
      id: "connector_graphrag_main",
      tenantId: "tenant_acme",
      connectorKey: "graphrag",
      status: "active",
      credentialsRef: createSecretRef("control_plane", "tenant_acme/graphrag"),
      config: { workspaceId: "knowledge_workspace" },
    },
  ],
  workflowPolicies: [
    {
      tenantId: "tenant_acme",
      workflowKey: "workflow.automation.run",
      enabled: true,
      allowedChannels: ["mobile"],
      allowedRoles: ["employee"],
    },
  ],
  workflowDefinitions: [
    {
      workflowKey: "workflow.automation.run",
      name: "Run automation",
      description: "Run a tenant-defined lightweight automation.",
      version: 1,
      inputSchema: { type: "object", required: ["automationId"] },
      outputSchema: { type: "object" },
      sideEffectClass: "none",
      stepSpec: { steps: [] },
    },
  ],
  identityRules: [
    {
      tenantId: "tenant_acme",
      channel: "mobile",
      userId: "user_123",
      displayName: "Alice Example",
    },
  ],
  runtimePolicies: {
    dmScope: "per-channel-peer",
    executionClassDefaults: { mobile: "approved_actions" },
    sandboxDefaults: { mode: "all", scope: "session", workspaceAccess: "none" },
    dangerousToolDeny: ["exec", "bash", "process", "gateway", "cron", "write", "edit"],
    customerCommandPolicy: {
      commandsEnabled: false,
      allowSafeUxCommands: true,
      allowedSafeCommands: ["help", "summary"],
    },
    auditPolicy: {
      logWorkflowRequests: true,
      logConnectorWrites: true,
    },
    workflowEngine: {
      kind: "local",
      config: { persistExecutions: true },
    },
    tools: {
      toolSearch: {
        enabled: false,
        mode: "tools",
      },
    },
  },
};

export const exampleRuntimeApply: RuntimeApply = {
  type: "runtime.apply",
  tenantId: "tenant_acme",
  generation: 12,
  snapshotHash: "sha256:snapshot-hash",
  snapshot: exampleTenantRuntimeSnapshot,
  reason: "operator_save",
  requestedBy: {
    actorType: "operator_user",
    actorId: "operator_001",
    tenantId: "tenant_acme",
    displayName: "Owner",
  },
  requestedAt: "2026-03-21T14:00:00Z",
};
